import React from "react";
import { Link } from "react-router-dom";
import { BedDoubleIcon, BathIcon, CarIcon, ArrowRightIcon, ImageIcon } from "lucide-react";
import { buttonClass } from "./Button";
import type { Design } from "../../data/designs";
type DesignCardProps = {
  design: Design;
  headingLevel?: "h3" | "h2";
};
export function DesignCard({
  design,
  headingLevel = "h3"
}: DesignCardProps) {
  const Heading = headingLevel;
  return <article className="group flex h-full flex-col overflow-hidden rounded-[28px] border border-line bg-white transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-charcoal/30 hover:shadow-[0_24px_48px_-28px_rgba(43,39,39,0.45)]" data-id="element-526" data-mp="1yfpq711b3qbw">
      <div className="relative aspect-[3/2] overflow-hidden bg-paper-deep" data-id="element-527" data-mp="23dixxyi9csea">
        {design.image ? <img src={design.image} alt={`${design.name} façade render`} width={698} height={468} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]" data-id="element-528" data-mp="1fneups8zeunt" /> : <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center" data-id="element-529" data-mp="2kb9nquecwv1a">
            <ImageIcon className="h-8 w-8 text-slate" aria-hidden="true" data-id="element-530" data-mp="2p4lzw51un9d5" />
            <p className="font-heading text-[18px] font-semibold text-charcoal" data-id="element-531" data-mp="2u2ni6nrgb4nk">Render coming soon</p>
            <p className="text-[14px] text-slate" data-id="element-532" data-mp="1wff4hexoyzw4">Ask us for the façade and floor plan</p>
          </div>}
        <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[13px] font-semibold text-charcoal" data-id="element-533" data-mp="21ehxg10edfnq">Single-storey</span>
        <span className="absolute right-4 top-4 rounded-full bg-charcoal px-3 py-1 text-[13px] font-semibold text-paper" data-id="element-534" data-mp="26f5xfc6lt6u3">{design.frontage} lot</span>
        {design.image && <span className="absolute bottom-3 right-3 rounded-full bg-paper/90 px-2.5 py-1 text-[11px] font-medium text-slate" data-id="element-535" data-mp="3hrowusqvnc1a">Render</span>}
      </div>

      <div className="flex flex-1 flex-col p-6 text-center md:p-7" data-id="element-536" data-mp="1mo0lj03jpl4o">
        <Heading className="text-[24px] font-semibold tracking-tight text-charcoal md:text-[28px]" data-id="element-537" data-mp="1hppbopd93amg">{design.name}</Heading>
        <p className="mx-auto mt-2 max-w-sm text-[16px] leading-snug text-slate" data-id="element-538" data-mp="1vq043wu7kx9q">{design.tagline}</p>

        <dl className="mt-6 grid grid-cols-3 divide-x divide-line rounded-2xl bg-paper py-4" data-id="element-539" data-mp="1qq5edwm66dq9">
          {[{
          label: "Bedrooms",
          value: design.bedrooms,
          icon: BedDoubleIcon
        }, {
          label: "Bathrooms",
          value: design.bathrooms,
          icon: BathIcon
        }, {
          label: "Car spaces",
          value: design.carSpaces,
          icon: CarIcon
        }].map(spec => <div key={spec.label} className="flex flex-col items-center gap-1" data-id="element-540" data-mp="17zw1nssrb3kb">
              <spec.icon className="h-5 w-5 text-slate" aria-hidden="true" data-id="element-541" data-mp="17xt3umld4651" />
              <dt className="order-last text-[13px] text-slate" data-id="element-542" data-mp="k8qltdmtgba0">{spec.label}</dt>
              <dd className="font-heading text-[20px] font-semibold" data-id="element-543" data-mp="fa71fhnvf7jr">{spec.value}</dd>
            </div>)}
        </dl>

        <dl className="mt-4 grid grid-cols-2 gap-4 text-[15px]" data-id="element-544" data-mp="2qxajoscf80b1">
          <div data-id="element-545" data-mp="2vqc054sa07wq">
            <dt className="text-slate" data-id="element-546" data-mp="2vq3k540uwmhb">Living area</dt>
            <dd className="mt-0.5 font-semibold" data-id="element-547" data-mp="2hy5ljn4ru8ze">{design.livingArea ? `${design.livingArea} m²` : "On request"}</dd>
          </div>
          <div data-id="element-548" data-mp="2my1nzhxtlscu">
            <dt className="text-slate" data-id="element-549" data-mp="2mxi43vom0tw6">Total under roof</dt>
            <dd className="mt-0.5 font-semibold" data-id="element-550" data-mp="3am1goneozsu2">{design.totalUnderRoof ? `${design.totalUnderRoof} m²` : "On request"}</dd>
          </div>
        </dl>
        {design.outdoorNote && <p className="mt-3 text-[14px] text-slate" data-id="element-551" data-mp="30odj7pppc787">{design.outdoorNote}</p>}

        <div className="mt-auto flex flex-col items-center gap-3 pt-7" data-id="element-552" data-mp="30o51nkimwdqo">
          <Link to={`/home-designs/${design.slug}`} className={buttonClass("secondary", "w-full sm:w-auto")} aria-label={`View design: ${design.name}`} data-id="element-553" data-mp="35m6n2c3j8p5b">
            View design
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-554" data-mp="1u8kzgvdbmvlw" />
          </Link>
          <Link to={`/contact?design=${design.slug}`} className="text-[15px] font-medium text-charcoal underline underline-offset-4 hover:decoration-2" data-id="element-555" data-mp="29hpuf71ty9t4">
            Enquire about {design.name.replace("The ", "")}
          </Link>
        </div>
      </div>
    </article>;
}